/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	Slider,
	IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Cropper from "react-easy-crop";

const ZoomControl = ({ zoom, setCropZoom, getInnerZoom }) => {
	const [innerZoom, setInnerZoom] = React.useState(zoom);
	React.useEffect(() => {
		getInnerZoom((newZoom) => setInnerZoom(newZoom));
	}, [getInnerZoom]);
	return (
		<>
			<Typography id="input-slider" style={{ fontSize: "12px", color: "#0000008A" }}>
				Zoom
			</Typography>
			<Slider
				size="small"
				value={innerZoom}
				min={0.3}
				max={3}
				step={0.01}
				valueLabelDisplay="off"
				aria-label="input-slider"
				onChange={(_event, newValue) => {
					setCropZoom(newValue);
					setInnerZoom(newValue);
				}}
				style={{ width: "200px" }}
			/>
		</>
	);
};

export const ImageCropDialog = ({
	title,
	subtitle,
	imageUrl,
	aspectRatio,
	dialogArguments,
	onCropComplete,
	open,
	onClose,
	cropperStyles = {},
	cropSize = undefined,
	actions,
	content,
	initialZoom,
	intitialSettings,
	submitPerRender,
	submitButtonText,
	getZoomControl,
}) => {
	const [crop, setCrop] = React.useState(cropSize || { x: 0, y: 0 });
	const [zoom, setZoom] = React.useState(!!initialZoom ? initialZoom : 1);
	const [setZoomBarZoom, setSetZoomBarZoom] = React.useState({ setInnerZoomFunction: () => {} });
	const [passBackVariables, setPassBackVariables] = React.useState([]);
	const [edited, setEdited] = React.useState(false);
	const [mediaSize, setMediaSize] = React.useState({});
	const [initialized, setInitialized] = React.useState(false);
	const closeDialogCheck = () => {
		if (!edited || window.confirm("You have unsaved changes, are you sure you wish to close the dialogue?")) {
			onClose();
		}
	};
	React.useEffect(() => {
		setZoom(initialZoom);
	}, [initialZoom]);

	React.useEffect(() => {
		getZoomControl(
			<div style={{ flex: 1 }}>
				<ZoomControl
					zoom={zoom}
					getInnerZoom={(innerZoomFunc) => {
						setSetZoomBarZoom({ setInnerZoomFunction: innerZoomFunc });
					}}
					setCropZoom={(newZoom) => {
						if (newZoom !== zoom) {
							setZoom(newZoom);
							setEdited(true);
						}
					}}
				/>
			</div>,
		);
		//For perfomance reasons we only want this to be passed back on first render.
		//Therefor we want this useEffect to only trigger once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<Dialog open={open} onClose={closeDialogCheck} {...dialogArguments} fullWidth>
			<DialogTitle>
				<div style={{ justifyContent: "space-between", display: "flex" }}>
					<div>{title}</div>
					<div>
						<IconButton size="small" style={{ margin: "0px" }} onClick={closeDialogCheck}>
							<CloseIcon />
						</IconButton>
					</div>
				</div>
				<Typography style={{ fintSize: "12px", color: "gray" }}>{subtitle}</Typography>
			</DialogTitle>
			<DialogContent
				style={{ height: "500px", backgroundColor: "gray", overflow: "hidden", position: "relative", padding: "0px" }}
			>
				<>
					<Cropper
						image={imageUrl}
						crop={crop}
						cropSize={cropSize}
						style={{ ...cropperStyles }}
						zoom={zoom}
						aspect={aspectRatio}
						onCropChange={setCrop}
						onCropComplete={(percentages, pixels) => {
							if (initialized) {
								if (submitPerRender) {
									onCropComplete(
										{ ...pixels, zoom, scale: (mediaSize.height / mediaSize.naturalHeight) * zoom },
										{ ...percentages, zoom, scale: (mediaSize.height / mediaSize.naturalHeight) * zoom },
									);
								}
								//Check deep equal to see if settings have change.
								//We round the numbers to avoid rounding errors.
								Object.keys(percentages).forEach((key) => {
									if (
										intitialSettings &&
										intitialSettings[key] !== undefined &&
										Math.round(percentages[key]) !== Math.round(intitialSettings[key])
									) {
										setEdited(true);
									}
								});
								setPassBackVariables([
									{ ...pixels, zoom, scale: (mediaSize.height / mediaSize.naturalHeight) * zoom },
									{ ...percentages, zoom, scale: (mediaSize.height / mediaSize.naturalHeight) * zoom },
								]);
							}
						}}
						onZoomChange={(newZoom) => {
							if (newZoom !== zoom) {
								setZoom(newZoom);
								setZoomBarZoom.setInnerZoomFunction(newZoom);
								setEdited(true);
							}
						}}
						restrictPosition={false}
						onMediaLoaded={(newMediaSize) => {
							//The media size of the image. This will contain both the natual height/width of the image and the pre-scaled cropper image sizes
							setMediaSize(newMediaSize);
							setInitialized(true);
						}}
						minZoom={0.1}
						objectFit="contain"
						initialCroppedAreaPercentages={intitialSettings}
					/>
					{content}
				</>
			</DialogContent>
			<DialogActions>
				{actions}
				<Button
					variant="outlined"
					size={"small"}
					onClick={() => {
						onCropComplete(...passBackVariables);
					}}
				>
					{submitButtonText}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
